import { Icon16MoreVertical, Icon20Arrow2SquarepathOutline, Icon20InfoCircleOutline } from '@vkontakte/icons';
import { Button, unstable_Popover as Popover } from '@vkontakte/vkui';
import React, { type FC, memo, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { sendDwhButton } from 'reactApp/modules/dwh/dwh.helpers';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { removeFromSafeAction, restoreFromSafeRequest } from 'reactApp/modules/trashbin/trashbin.module';
import { renderSafePromoDialog, SafePromoType } from 'reactApp/ui/Toolbar/Move2SafeButton/SafePromoDialog';

import styles from './SafeManageButton.css';

const name = 'safe-dropdown';

export const SafeManageButton: FC = memo(() => {
    const [shown, setShown] = useState(false);
    const dispatch = useDispatch();
    const storage = useSelector(getCurrentStorage);
    const source = storage === EStorageType.trashbin ? 'trashbin' : 'cleaner';

    const handleDropdownClick = useCallback(
        (shown: boolean) => {
            sendDwhButton({ action: shown ? 'click' : 'close', name, source });
            setShown(shown);
        },
        [setShown, source]
    );

    useEffect(() => {
        sendDwhButton({ name, source });
    }, []);

    const handleClickRestore = useCallback(() => {
        sendDwhButton({ action: 'click', name: 'safe-dropdown-restore', source });
        dispatch(restoreFromSafeRequest());
    }, []);

    const handleClickClear = useCallback(() => {
        sendDwhButton({ action: 'click', name: 'safe-dropdown-clear', source });
        dispatch(removeFromSafeAction());
    }, []);

    const handleClickAbout = useCallback(() => {
        sendDwhButton({ action: 'click', name: 'safe-dropdown-about', source });
        setShown(false);
        renderSafePromoDialog({ promoType: SafePromoType.moreAboutSafe });
    }, []);

    const handleManage = useCallback(() => {
        sendDwhButton({ action: 'click', name: 'safe-dropdown-manage', source });
        setShown(false);
        renderSafePromoDialog({ promoType: SafePromoType.manage, onClickRestore: handleClickRestore, onClickClearSafe: handleClickClear });
    }, [handleClickRestore, handleClickClear]);

    return (
        <div className={styles.wrapper}>
            <Button mode="primary" appearance="accent" size={'s'} data-test-id="trashbin-safe-banner-button" onClick={handleClickRestore}>
                Вернуть файлы в Облако
            </Button>
            <Popover
                action="click"
                shown={shown}
                placement="bottom-end"
                onShownChange={handleDropdownClick}
                className={styles.popover}
                content={
                    <div className={styles.contentWrapper} data-test-id="safe-dropdown-content">
                        <Button
                            mode="tertiary"
                            before={<Icon20InfoCircleOutline />}
                            className={styles.contentButton}
                            data-test-id="restore-files"
                            onClick={handleClickAbout}
                        >
                            <span className={styles.text}>Подробнее о сейфе</span>
                        </Button>
                        <Button
                            mode="tertiary"
                            before={<Icon20Arrow2SquarepathOutline />}
                            className={styles.contentButton}
                            data-test-id="remove-files"
                            onClick={handleManage}
                        >
                            <span className={styles.text}>Управлять сейфом</span>
                        </Button>
                    </div>
                }
            >
                <Button
                    className={styles.dropdown}
                    mode="secondary"
                    appearance="neutral"
                    size={'s'}
                    after={<Icon16MoreVertical />}
                    data-test-id="safe-dropdown"
                />
            </Popover>
        </div>
    );
});

SafeManageButton.displayName = 'SafeManageButton';
